var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AggregateError } from './aggregate_error';
import { NestedError } from './nested_error';
/**
 * Waits all promises to be completed (either resolved or rejected).
 * If at least one promise was rejected throws an AggregateError with all of the errors from rejected promises.
 * @param values An array of Promises.
 * @returns A new Promise that is completed when all of the given promises are done.
 */
export function waitAll(values) {
    return __awaiter(this, void 0, void 0, function* () {
        const errors = [];
        const results = yield Promise.all(values.map((promise) => __awaiter(this, void 0, void 0, function* () {
            try {
                return yield promise;
            }
            catch (e) {
                errors.push(e instanceof Error ? e : new NestedError('Promise was rejected with', e));
                return undefined;
            }
        })));
        if (errors.length !== 0) {
            throw new AggregateError(errors);
        }
        return results;
    });
}
/**
 * Waiting until some condition is met, or timeout is reached. The condition is checked every `interval` milliseconds.
 *
 * If the condition is not met within `timeout` milliseconds, the promise is rejected with an Error.
 * If the condition is met, the promise is resolved with the value returned by the waiter.
 *
 * @param waiter A function that returns a value or undefined if the condition is not met yet
 * @param {Object} options - options for configuring the wait behavior
 * @param {number} options.timeout - timeout value in milliseconds.
 * @param {number} options.interval - interval value in milliseconds.
 * @param {function} [options.timeoutError] - optional function that returns an error to be thrown in case of timeout.
 * @param {function} [options.onWaiterError] - optional callback that receives an exception thrown by the waiter.
 * Exceptions thrown after the timeout has already occurred, are ignored.
 * The callback can optionally return an error, which will stop `waitUntil` immediately and rejects the promise with the returned error.
 * If you only want to log exceptions, but otherwise keep `waitUntil` going on, then do not return an error from the callback.
 * @returns The successful response from the waiter
 */
export function waitUntil(waiter_1) {
    return __awaiter(this, arguments, void 0, function* (waiter, options = {
        timeout: 5000,
        interval: 200,
    }) {
        return yield new Promise((resolve, reject) => {
            let timeout;
            let timedOut = false;
            let busy = false;
            const interval = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (busy) {
                    return; // only queue next waiter once previous one finishes
                }
                let value;
                try {
                    busy = true;
                    value = yield waiter();
                }
                catch (e) {
                    if (timedOut) {
                        return; // swallow whatever error comes after we already timed out
                    }
                    const err = (_a = options.onWaiterError) === null || _a === void 0 ? void 0 : _a.call(options, e);
                    if (err === e || err instanceof Error) {
                        clearTimeout(timeout);
                        clearInterval(interval);
                        return reject(err);
                    }
                }
                finally {
                    busy = false;
                }
                if (value !== undefined) {
                    clearTimeout(timeout);
                    clearInterval(interval);
                    return resolve(value);
                }
            }), options.interval);
            timeout = setTimeout(() => {
                var _a, _b;
                timedOut = true;
                clearInterval(interval);
                reject((_b = (_a = options.timeoutError) === null || _a === void 0 ? void 0 : _a.call(options)) !== null && _b !== void 0 ? _b : new Error('Timeout'));
            }, options.timeout);
        });
    });
}
//# sourceMappingURL=promise.js.map